import { Controller } from "@hotwired/stimulus"

// Self-heals group match photo cells without a refresh.
//
// Each pending cell (the other person's photo/blur not in yet, or not revealed)
// carries data-pending-cell="<discoveryId>-<side>" and data-cell-state="<state>".
// This controller polls the server with the on-screen pending cells + their current
// state; the server returns fresh HTML only for cells whose state changed, which
// Turbo swaps in place. Once a cell settles (shows the real photo) it loses
// data-pending-cell and drops out of the poll.
//
// This replaces the old per-member ActionCable broadcast, which was unreliable in the
// per-tab native webview (subscriptions drop on navigation and miss messages). Polls
// only while the page is visible and stops when nothing is pending.
export default class extends Controller {
  static values = { url: String, interval: { type: Number, default: 4000 } }

  connect() {
    this._onVisibility = this._onVisibility.bind(this)
    document.addEventListener("visibilitychange", this._onVisibility)
    this._start()
  }

  disconnect() {
    this._stop()
    document.removeEventListener("visibilitychange", this._onVisibility)
  }

  _onVisibility() {
    document.hidden ? this._stop() : this._start()
  }

  _start() {
    if (this._timer || document.hidden) return
    this._timer = setInterval(() => this._tick(), this.intervalValue)
  }

  _stop() {
    if (this._timer) { clearInterval(this._timer); this._timer = null }
  }

  async _tick() {
    const cells = Array.from(this.element.querySelectorAll("[data-pending-cell]"))
    if (cells.length === 0) { this._stop(); return }
    if (this._inflight) return

    this._inflight = true
    try {
      const tokens = cells.map((el) => `${el.dataset.pendingCell}:${el.dataset.cellState || ""}`)
      const res = await fetch(`${this.urlValue}?cells=${encodeURIComponent(tokens.join(","))}`, {
        headers: { "Accept": "text/vnd.turbo-stream.html", "X-Requested-With": "XMLHttpRequest" }
      })
      if (res.ok) {
        const html = await res.text()
        if (html.trim()) window.Turbo.renderStreamMessage(html)
      }
    } catch (e) {
      // transient — try again next tick
    } finally {
      this._inflight = false
    }
  }
}
